import React, { useState } from "react";
import { object, array, bool, string, number } from "prop-types";
import get from "lodash/get";
import cx from "classnames";
import { ResponsiveImage } from "@quintype/components";

import Headline from "../headline";
import CrossIcon from "../icons/cross-icon";
import AuthorsView from "../authors-view";

import { isAuthorNameShown, getHeadline } from "../../utils/utils";

import "./cardImage.m.css";

const CardImage = ({
  story,
  aspectRatio,
  defaultWidth,
  imageType,
  widths,
  eager,
  showAlternateImage,
  showAuthorName,
  showHeadline,
  showImageCaption,
}) => {
  const [isImageCaptionOpen, setIsImageCaptionOpen] = useState(false);

  const showSummaryClick = () => setIsImageCaptionOpen(!isImageCaptionOpen);

  const wrapperStyleName = cx(
    "qt-image-dynamic",
    { image16x9: imageType === "image16x9" },
    { image4x3: imageType === "image4x3" },
    { image1x1: imageType === "image1x1" },
    { image2x1: imageType === "image2x1" },
    { image3x4: imageType === "image3x4" },
    { image3x2: imageType === "image3x2" },
    { image2x3: imageType === "image2x3" },
    { image5x4: imageType === "image5x4" },
    { image6x4: imageType === "image6x4" }
  );

  const showAuthorNameView = () => {
    return isAuthorNameShown(story) ? (
      <span styleName="author-name">
        <AuthorsView story={story} />
      </span>
    ) : null;
  };

  const showHeadlineView = () => (
    <div styleName="headline-view" className="cardimage-headline-view">
      <Headline text={getHeadline(story)} headerType={4} headerLevel={2} story={story} />
    </div>
  );

  const showImageCaptionView = () => {
    if (!(story["hero-image-caption"] && story["hero-image-attribution"])) {
      return null;
    }

    if (isImageCaptionOpen) {
      return (
        <div styleName="image-caption-view">
          <div styleName="image-caption-text">
            <span dangerouslySetInnerHTML={{ __html: story["hero-image-caption"] }} />
            <span
              styleName="hero-image-attribution"
              dangerouslySetInnerHTML={{ __html: story["hero-image-attribution"] }}
            />
          </div>
          <div styleName="close-icon" onClick={showSummaryClick}>
            <CrossIcon />
          </div>
        </div>
      );
    }

    return (
      <div styleName="iCircle" onClick={showSummaryClick}>
        i
      </div>
    );
  };

  let heroImage = "";

  if (showAlternateImage && get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"])) {
    heroImage = story.alternative.home.default["hero-image"]["hero-image-s3-key"];
  } else if (story["hero-image-s3-key"]) {
    heroImage = story["hero-image-s3-key"];
  }

  function captureClickedDOMNode(e) {
    const infiniteScrollEleNumber = e.target.closest("div[data-infinite-scroll]").getAttribute("data-infinite-scroll");
    window.sessionStorage.setItem("clicked_node", infiniteScrollEleNumber);
  }

  return (
    <div styleName="card-image-wrapper" onClick={captureClickedDOMNode} className="card-image-wrapper">
      <figure styleName={wrapperStyleName} className={`${wrapperStyleName} qt-figure`}>
        {heroImage && (
          <picture className="qt-image">
            <ResponsiveImage // Desktop WebP
              slug={heroImage}
              metadata={story["hero-image-metadata"]}
              aspectRatio={aspectRatio}
              defaultWidth={defaultWidth}
              widths={widths}
              type="image/webp"
              sizes="( max-width: 500px ) 98%, ( max-width: 875px ) 48%, 23%"
              imgParams={{ auto: ["format", "compress"], fmt: "webp" }}
              styleName="image"
              className="image"
              alt={story["hero-image-caption"] || story.headline}
              eager={eager}
              imageCDN="images.thequint.com"
            />
            {/* <ResponsiveImage // Desktop Jpeg
              slug={heroImage}
              metadata={story["hero-image-metadata"]}
              aspectRatio={aspectRatio}
              defaultWidth={defaultWidth}
              widths={widths}
              type="image/webp"
              sizes="( max-width: 500px ) 98%, ( max-width: 875px ) 48%, 23%"
              imgParams={{ auto: ["format", "compress"] }}
              styleName="image"
              className="image"
              alt={story["hero-image-caption"] || story.headline}
              eager={eager}
            /> */}
          </picture>
        )}
      </figure>
      {showAuthorName && showAuthorNameView()}
      {showHeadline && showHeadlineView()}
      {showImageCaption && showImageCaptionView()}
    </div>
  );
};

CardImage.defaultProps = {
  story: {},
  aspectRatio: [1, 1],
  imageType: "image1x1",
  widths: [250, 480, 764],
  eager: false,
  showAlternateImage: false,
  showAuthorName: false,
  showHeadline: false,
  showImageCaption: false,
};

CardImage.propTypes = {
  story: object,
  aspectRatio: array,
  defaultWidth: number,
  imageType: string,
  widths: array,
  eager: bool,
  showAlternateImage: bool,
  showAuthorName: bool,
  showHeadline: bool,
  showImageCaption: bool,
};

export default CardImage;
